import {collectAttachments} from './attachment-gc';
import type { Capture, CaptureRevision } from '../domain/capture/model';
import { NookError } from '../domain/shared/errors';
import { NookDatabase, assertWritable, markSearchDirty } from '../infrastructure/database/database';
import { assertCapacity, sha256 } from '../infrastructure/backup/snapshot';

export class HistoryService {
  constructor(readonly db:NookDatabase){}
  async change(kind:'trash'|'restore'|'purge'|'restore-revision', entity:'capture'|'draft', id:string, expectedRevision:number, operationId:string, historyId?:string):Promise<void> {
    const fingerprint=await sha256(new Blob([JSON.stringify({kind,entity,id,expectedRevision,historyId})]));
    await this.db.transaction('rw',this.db.tables,async()=>{
      await assertWritable(this.db);
      const done=await this.db.operations.get(operationId);
      if(done) { if(done.fingerprint!==fingerprint || done.kind!==kind) throw new NookError('operation','操作标识已被使用。'); return; }
      const table=entity==='capture'?this.db.captures:this.db.drafts;
      const current=await table.get(id);
      if(!current || current.revision!==expectedRevision) throw new NookError('conflict','记录已变化，未覆盖另一处的新内容。请重新查看。');
      const now=new Date().toISOString();
      if(kind==='purge') {
        if(!current.deletedAt) throw new NookError('validation','只能永久删除回收站中的记录。');
        if(entity==='capture' && await this.db.drafts.where('captureId').equals(id).count()) throw new NookError('validation','此记录仍有关联草稿（包括回收站）。请先处理这些草稿，避免丢失引用。');
        if(entity==='capture'&&[...await this.db.itemDrafts.toArray(),...await this.db.contentDrafts.toArray()].some(d=>d.sourceCaptureId===id))throw new NookError('validation','仍有整理草稿引用此记录，请先处理草稿');
        if(entity==='capture'&&(await this.db.relations.where('captureId').equals(id).toArray()).some(r=>!r.revokedAt))throw new NookError('validation','此捕捉仍有整理关系，请先撤销整理。目标对象不会删除。');
        await table.delete(id);
        if(entity==='capture') await this.db.revisions.where('captureId').equals(id).delete();
        await collectAttachments(this.db);
      } else {
        let next={...current,revision:current.revision+1,updatedAt:now};
        let reason:CaptureRevision['reason']=kind==='trash'?'trashed':'restored';
        if(kind==='restore-revision') {
          if(entity!=='capture' || current.deletedAt) throw new NookError('validation','先从回收站恢复记录，再选择历史版本。');
          const historical=await this.db.revisions.get(historyId??'');
          if(!historical || !('captureId'in historical) || historical.captureId!==id) throw new NookError('missing','历史版本不存在。');
          next={...next,body:historical.snapshot.body,attachmentIds:[...historical.snapshot.attachmentIds]}; reason='history-restored';
        } else {
          if((kind==='trash')===!!current.deletedAt) throw new NookError('conflict','记录状态已变化，请刷新查看。');
          next={...next,deletedAt:kind==='trash'?now:null};
        }
        if(entity==='capture') {
          const snapshot=next as Capture; await this.db.captures.put(snapshot);
          await this.db.revisions.add({id:`${id}:${next.revision}`,captureId:id,revision:next.revision,recordedAt:now,reason,snapshot});
        } else await this.db.drafts.put(next as typeof current & {captureId:string;baseRevision:number;state:'active'|'conflict'});
      }
      await this.db.operations.add({id:operationId,kind,fingerprint,resultId:id,resultRevision:expectedRevision+1,committedAt:now});
      await markSearchDirty(this.db);if(kind!=='purge')await assertCapacity(this.db);
    });
  }
}
